import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import { addMeasurementUnits } from "../../../../../services/Admin/MaterialsApi";

const defaultUnit = () => ({ name: "", abbreviation: "" });

const AddUnitsDialog = ({ open, onClose, onAdded }) => {
	const [units, setUnits] = useState([defaultUnit()]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	const handleChange = (index, field, value) => {
		setUnits((prev) => {
			const copy = [...prev];
			copy[index] = { ...copy[index], [field]: value };
			return copy;
		});
	};

	const addRow = () => setUnits((prev) => [...prev, defaultUnit()]);

	const removeRow = (index) => setUnits((prev) => prev.filter((_, i) => i !== index));

	const reset = () => {
		setUnits([defaultUnit()]);
		setError("");
		setSuccess("");
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError("");
		setSuccess("");
		try {
			const payload = units
				.filter((u) => u.name || u.abbreviation)
				.map((u) => ({
					name: String(u.name || "").trim(),
					abbreviation: String(u.abbreviation || "").trim(),
					name_en: "",
				}));
			if (!payload.length) {
				setError("الرجاء إدخال وحدة واحدة على الأقل");
				setLoading(false);
				return;
			}

			const res = await addMeasurementUnits(payload);
			setSuccess(res?.message || "تمت الإضافة بنجاح");
			if (typeof onAdded === "function") onAdded(res?.data || null);
			// keep dialog open and clear form for next entries
			reset();
		} catch (err) {
			setError(err?.message || "فشل في إضافة وحدات القياس");
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		reset();
		if (typeof onClose === "function") onClose();
	};

	return (
		<Dialog open={Boolean(open)} onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>إضافة وحدات قياس</DialogTitle>
			<DialogContent>
				<Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2, mt: 1 }}>
					{error && <Alert severity="error">{error}</Alert>}
					{success && <Alert severity="success">{success}</Alert>}

					{units.map((u, idx) => (
						<Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
							<TextField label="الاسم" value={u.name} onChange={(e) => handleChange(idx, "name", e.target.value)} fullWidth required />
							<TextField label="الاختصار" value={u.abbreviation} onChange={(e) => handleChange(idx, "abbreviation", e.target.value)} sx={{ width: 140 }} />
							<IconButton aria-label="remove" onClick={() => removeRow(idx)} disabled={units.length === 1}>
								<RemoveCircleOutlineIcon />
							</IconButton>
						</Box>
					))}

					<Box sx={{ display: "flex", gap: 1 }}>
						<Button startIcon={<AddIcon />} onClick={addRow}>أضف صف</Button>
					</Box>

					<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
						<Button onClick={handleClose} color="inherit">إلغاء</Button>
						<Button type="submit" variant="contained" disabled={loading}>
							{loading ? <CircularProgress size={20} /> : "حفظ"}
						</Button>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions />
		</Dialog>
	);
};

export default AddUnitsDialog;
